import React, { useEffect } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { motion } from 'motion/react';
import {
  FaArrowRight, FaShieldAlt, FaBolt, FaLock, FaChartLine, FaCheck, FaAngleRight,
  FaExternalLinkAlt,
} from 'react-icons/fa';
import PageNavbar from './PageNavbar';
import Footer from './Footer';
import {
  defaultViewport,
  staggerContainer,
  staggerItem,
  getRevealProps,
} from '../motion/scrollMotion';
import { getProductBySlug, allProducts } from '../config/productsMenuConfig';
import './ServiceDetailPage.css';
import './ProductDetailPage.css';

const capIcons = [<FaShieldAlt />, <FaBolt />, <FaLock />, <FaChartLine />];

const ProductDetailPage = () => {
  const { slug } = useParams();
  const product = getProductBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (product) {
      document.title = product.seoTitle || `${product.label} | Trinex360`;
      const metaDesc = document.querySelector('meta[name="description"]');
      const content = product.seoDesc || product.hero.desc;
      if (metaDesc) {
        metaDesc.setAttribute('content', content);
      } else {
        const metaEl = document.createElement('meta');
        metaEl.name = 'description';
        metaEl.content = content;
        document.head.appendChild(metaEl);
      }
    }
  }, [slug, product]);

  if (!product) {
    return <Navigate to="/products" replace />;
  }

  const { hero, stats = [], capabilities = [], how = [], why, external, cta } = product;
  const related = allProducts.filter((p) => p.slug !== product.slug);
  const isPreview = product.status === 'preview';

  // Primary hero action: launch the live platform if we have one, else contact.
  const primaryAction = external ? (
    <a href={external.url} target="_blank" rel="noopener noreferrer" className="svc-btn svc-btn-primary">
      {external.label} <FaExternalLinkAlt />
    </a>
  ) : (
    <Link to="/contact" className="svc-btn svc-btn-primary">
      {cta?.label || 'Get In Touch'} <FaArrowRight />
    </Link>
  );

  return (
    <div className="svc-page">
      <PageNavbar />

      {/* ---------- Hero ---------- */}
      <section className="svc-hero">
        <div className="svc-hero-glow" />
        <div className="svc-container svc-hero-inner">
          <motion.div className="svc-hero-text" {...getRevealProps('fadeUp')}>
            <nav className="svc-breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <FaAngleRight />
              <Link to="/products">Products</Link>
              <FaAngleRight />
              <span>{product.label}</span>
            </nav>

            <div className="prod-eyebrow-row">
              <span className="svc-eyebrow">{hero.eyebrow}</span>
              <span className={`prod-status prod-status-${product.status}`}>
                {isPreview ? 'In preview' : 'Available now'}
              </span>
            </div>
            <h1 className="svc-hero-title">{hero.title}</h1>
            <p className="prod-headline">{hero.headline}</p>
            <p className="svc-hero-desc">{hero.desc}</p>

            {hero.badges && (
              <ul className="prod-badges">
                {hero.badges.map((b) => (
                  <li key={b}><FaCheck /> {b}</li>
                ))}
              </ul>
            )}

            <div className="svc-hero-actions">
              {primaryAction}
              <Link to="/products" className="svc-btn svc-btn-ghost">
                All Products
              </Link>
            </div>
          </motion.div>

          {(stats.length > 0 || (hero.badges && hero.badges.length > 0)) && (
            <motion.aside className="svc-hero-card" {...getRevealProps('slideRight', 0.12)}>
              <span className="svc-card-tag">At a glance</span>
              <div className="svc-stats">
                {stats.map((s) => (
                  <div className="svc-stat" key={s.label}>
                    <div className="svc-stat-value">{s.value}</div>
                    <div className="svc-stat-label">{s.label}</div>
                  </div>
                ))}
              </div>
              {external && (
                <a
                  href={external.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="prod-card-external"
                >
                  {external.url.replace(/^https?:\/\//, '').replace(/\/$/, '')} <FaExternalLinkAlt />
                </a>
              )}
            </motion.aside>
          )}
        </div>
      </section>

      {/* ---------- Capabilities ---------- */}
      <section className="svc-section">
        <div className="svc-container">
          <motion.header className="svc-section-head" {...getRevealProps('fadeUp')}>
            <span className="svc-section-tag">Capabilities</span>
            <h2 className="svc-section-title">What {product.label} does</h2>
            <p className="svc-section-sub">
              Purpose-built capability, delivered as one focused product — not a bolt-on to a
              generic platform.
            </p>
          </motion.header>

          <motion.div
            className="svc-feature-grid prod-cap-grid"
            initial="hidden"
            whileInView="visible"
            viewport={defaultViewport}
            variants={{
              ...staggerContainer,
              visible: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
            }}
          >
            {capabilities.map((c, i) => (
              <motion.article className="svc-feature-card" key={c.title} variants={staggerItem}>
                <div className="svc-feature-icon">{capIcons[i % capIcons.length]}</div>
                <h3 className="svc-feature-title">{c.title}</h3>
                <p className="svc-feature-desc">{c.desc}</p>
              </motion.article>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ---------- How it works ---------- */}
      {how.length > 0 && (
        <section className="svc-section svc-section-alt">
          <div className="svc-container">
            <motion.header className="svc-section-head" {...getRevealProps('fadeUp')}>
              <span className="svc-section-tag">How It Works</span>
              <h2 className="svc-section-title">Up and running fast</h2>
              <p className="svc-section-sub">
                A simple path from setup to measurable outcomes.
              </p>
            </motion.header>

            <motion.ol
              className="svc-process"
              initial="hidden"
              whileInView="visible"
              viewport={defaultViewport}
              variants={{
                ...staggerContainer,
                visible: { transition: { staggerChildren: 0.1, delayChildren: 0.05 } },
              }}
            >
              {how.map((m) => (
                <motion.li className="svc-step" key={m.step} variants={staggerItem}>
                  <div className="svc-step-num">{m.step}</div>
                  <h3 className="svc-step-name">{m.name}</h3>
                  <p className="svc-step-desc">{m.desc}</p>
                </motion.li>
              ))}
            </motion.ol>
          </div>
        </section>
      )}

      {/* ---------- Why it matters ---------- */}
      {why && (
        <section className="svc-section">
          <div className="svc-container svc-why-grid">
            <motion.div className="svc-why-text" {...getRevealProps('slideLeft')}>
              <span className="svc-section-tag">Why It Matters</span>
              <h2 className="svc-section-title">{why.title}</h2>
              <p className="svc-why-body">{why.body}</p>
            </motion.div>

            <motion.ul className="svc-why-list" {...getRevealProps('slideRight', 0.12)}>
              {why.bullets.map((b) => (
                <li key={b}>
                  <span className="svc-why-check"><FaCheck /></span>
                  {b}
                </li>
              ))}
            </motion.ul>
          </div>
        </section>
      )}

      {/* ---------- Other products ---------- */}
      {related.length > 0 && (
        <section className="svc-section svc-section-alt">
          <div className="svc-container">
            <motion.header className="svc-section-head" {...getRevealProps('fadeUp')}>
              <span className="svc-section-tag">Explore More</span>
              <h2 className="svc-section-title">More Trinex360 products</h2>
            </motion.header>

            <div className="svc-related-grid">
              {related.map((r) => (
                <Link to={r.href} className="svc-related-card" key={r.slug}>
                  <span className="svc-related-name">{r.label}</span>
                  <FaArrowRight />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ---------- CTA ---------- */}
      <section className="svc-cta">
        <div className="svc-container">
          <motion.div className="svc-cta-card" {...getRevealProps('scaleUp')}>
            <h2 className="svc-cta-title">
              {isPreview
                ? `Want early access to ${product.label}?`
                : `Ready to get started with ${product.label}?`}
            </h2>
            <p className="svc-cta-desc">
              Talk to our team about {product.label} — we respond to every inquiry within one
              business day.
            </p>
            <div className="svc-cta-actions">
              {external ? (
                <a
                  href={external.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="svc-btn svc-btn-primary"
                >
                  {external.label} <FaExternalLinkAlt />
                </a>
              ) : (
                <Link to="/contact" className="svc-btn svc-btn-primary">
                  {cta?.label || 'Get In Touch'} <FaArrowRight />
                </Link>
              )}
              <Link to="/products" className="svc-btn svc-btn-ghost-light">
                View All Products
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProductDetailPage;
